import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { AirflowCalendarGranularity } from '../../types';
import { buildCellMap } from './utils';

type ViewMode = 'total' | 'failed';

/*
 * Manages the DAG Calendar tab: month navigation, granularity/view-mode toggles, and the
 * day-hour cell map derived from the DAG's runs in the visible month
 */
export function useCalendarTab(dagId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [monthDate, setMonthDate] = useState(() => new Date());
    const [granularity, setGranularity] = useState<AirflowCalendarGranularity>('daily');
    const [viewMode, setViewMode] = useState<ViewMode>('total');
    const [monthTotal, setMonthTotal] = useState(0);
    const [monthFailed, setMonthFailed] = useState(0);
    const [cellMap, setCellMap] = useState<Map<string, { total: number; failed: number }>>(new Map());
    const [isLoading, setIsLoading] = useState(true);

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const fetchCalendar = useCallback(async () => {
        setIsLoading(true);
        try {
            const monthStart = new Date(year, month, 1, 0, 0, 0);
            const monthEnd = new Date(year, month, daysInMonth, 23, 59, 59);
            const response = await AirflowDashboardHandler.getDagCalendar(Env, dagId, {
                logicalDateGte: monthStart.toISOString(),
                logicalDateLte: monthEnd.toISOString(),
            });
            setCellMap(buildCellMap(response.dag_runs, granularity));
            setMonthTotal(response.dag_runs.length);
            setMonthFailed(response.dag_runs.filter((entry) => entry.state === 'failed').length);
        } catch {
            errorAlert('Failed to load calendar data.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId, year, month, daysInMonth, granularity]);

    useEffect(() => {
        fetchCalendar();
    }, [fetchCalendar, refreshKey]);

    const maxCount = useMemo(() => {
        let max = 0;
        cellMap.forEach((cell) => {
            max = Math.max(max, viewMode === 'failed' ? cell.failed : cell.total);
        });
        return max;
    }, [cellMap, viewMode]);

    const successRate = monthTotal > 0 ? Math.round(((monthTotal - monthFailed) / monthTotal) * 100) : 0;

    /*
     * Moves the visible month backward or forward by one
     */
    const handleMonthChange = useCallback((delta: number) => {
        setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    }, []);

    return {
        year,
        month,
        daysInMonth,
        granularity,
        setGranularity,
        viewMode,
        setViewMode,
        monthTotal,
        monthFailed,
        successRate,
        cellMap,
        maxCount,
        isLoading,
        handleMonthChange,
    };
}
